import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Crown, ArrowRight } from "@phosphor-icons/react";

/**
 * ChampionshipCountdown — hero banner shown from Day 25 onwards.
 *
 * Behaviour by season state:
 *   • Day 25-29 (NATIONAL_SEASON): shows "Championship starts in Xd Xh"
 *     + user's live rank inside their country ("You're #7 in Ghana — 3 spots
 *     from qualifying").
 *   • Day 30 (NATIONAL_FREEZE): shows "Championship begins at 00:00 UTC"
 *     + qualified/not-qualified message.
 *   • Day 31 (INTERNATIONAL): shows animated red "LIVE — Watch now" pill
 *     linking to the public watch page.
 *   • Otherwise: renders nothing.
 */
export default function ChampionshipCountdown() {
  const { user } = useAuth();
  const [season, setSeason] = useState(null);
  const [rank, setRank] = useState(null);
  const [myStatus, setMyStatus] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    api.get("/season/current").then((r) => setSeason(r.data)).catch(() => {});
    api.get("/season/profile/me").then((r) => setRank(r.data?.season_rank)).catch(() => {});
    api.get("/championship/my-status").then((r) => setMyStatus(r.data)).catch(() => {});
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!season) return null;
  const day = season.day;
  const state = season.state;
  // Show early if: any admin-scheduled kickoff exists OR user has an
  // accepted invite OR champ is running/scheduled. Otherwise wait until Day 25.
  const hasEarlySignal = Boolean(
    myStatus?.scheduled_at
    || myStatus?.invite
    || myStatus?.champ_state === "RUNNING"
    || myStatus?.role === "qualifier"
  );
  if (day < 25 && !hasEarlySignal) return null;

  // "Live" = the tournament is ACTUALLY running RIGHT NOW. Not just because
  // the calendar hit Day 31. This prevents a permanent "LIVE" state showing
  // when no championship has been launched or when a previous run has ENDED.
  const isLive = myStatus?.champ_state === "RUNNING";
  const isFreeze = state === "NATIONAL_FREEZE";
  const secsToChamp = Math.max(0, Math.floor((season.international_in || 0)));

  // If admin has scheduled a specific kickoff time, count down to THAT
  // instead of the day-cycle heuristic.
  const scheduledAt = myStatus?.scheduled_at;
  const scheduledMs = scheduledAt ? new Date(scheduledAt).getTime() : null;
  const secsToScheduled = scheduledMs ? Math.max(0, Math.floor((scheduledMs - now) / 1000)) : null;
  const showScheduled = scheduledMs != null && secsToScheduled > 0;

  // Am I accepted for the championship?
  const inviteStatus = myStatus?.invite?.status;
  const iAmAccepted = inviteStatus === "accepted";

  const country = user?.country;
  const qualifyingCountries = ["NG", "GH"];
  const isQualifying = qualifyingCountries.includes(country);

  // Personalize CTA based on real status
  const role = myStatus?.role;
  const ctaLabel = isLive
    ? (role === "contender" ? "Play now" : role === "eliminated" ? "Watch (out)" : "Watch live")
    : iAmAccepted
      ? "See lobby & countdown"
      : role === "qualifier" ? "Confirm my seat"
      : isFreeze ? "See standings"
      : "Play & climb";
  const ctaTo = "/championship/live";

  return (
    <div
      className={`nk-card p-5 border-l-2 ${isLive ? "border-nk-bad animate-pulse-soft" : "border-nk-gold"}`}
      data-testid="championship-countdown"
    >
      <div className="flex items-start gap-4 flex-wrap">
        <Crown size={28} weight="fill" className={`shrink-0 mt-1 ${isLive ? "text-nk-bad" : "text-nk-gold"}`} />
        <div className="flex-1 min-w-[220px]">
          <p className="eyebrow mb-1.5" style={{ color: isLive ? "#ff5a5a" : "var(--nk-gold)" }}>
            {isLive ? "LIVE NOW" : "Continental Championship"}
          </p>
          <h3 className="font-display text-2xl sm:text-3xl font-medium tracking-tight">
            {isLive ? (
              <>The Championship is <span className="italic font-light text-nk-bad">live</span>.</>
            ) : showScheduled ? (
              <>Kickoff in <span className="text-nk-gold font-mono tabular-nums">{formatCountdown(secsToScheduled, now)}</span></>
            ) : isFreeze ? (
              <>Season is <span className="italic font-light text-nk-gold">frozen</span>. Championship begins soon.</>
            ) : (
              <>Championship in <span className="text-nk-gold font-mono tabular-nums">{formatCountdown(secsToChamp, now)}</span></>
            )}
          </h3>

          {/* Personal qualification hint */}
          {iAmAccepted && showScheduled && (
            <p className="text-sm text-nk-mute mt-2">
              <span className="text-nk-gold">✓ Seat confirmed</span> · You&apos;re in the arena.
              Kickoff {new Date(scheduledMs).toLocaleString(undefined, {
                weekday: "short", hour: "2-digit", minute: "2-digit",
              })}.
            </p>
          )}
          {!iAmAccepted && !isLive && isQualifying && rank && (
            <p className="text-sm text-nk-mute mt-2">
              You&apos;re <span className="text-nk-text font-medium">#{rank}</span> in {country === "GH" ? "Ghana" : "Nigeria"}
              {rank <= 5
                ? <> — <span className="text-nk-good font-medium">qualifying spot ✓</span></>
                : <> — <span className="text-nk-warn">{rank - 5} spot{rank - 5 === 1 ? "" : "s"} from qualifying</span></>}
            </p>
          )}
          {!isLive && !isQualifying && !iAmAccepted && (
            <p className="text-sm text-nk-mute mt-2">
              Ghana vs Nigeria on Day 31 — the rest of Africa watches (and plays for practice; scores pause).
            </p>
          )}
          {isLive && (
            <p className="text-sm text-nk-text mt-2">
              10 champions. 4 themed rounds. One continental crown. Watch it unfold now.
            </p>
          )}
        </div>

        <Link
          to={ctaTo}
          data-testid="championship-countdown-cta"
          className={`shrink-0 self-start inline-flex items-center gap-1.5 px-4 py-2 rounded-sm text-sm font-medium transition-all ${
            role === "contender" && isLive
              ? "bg-nk-gold text-nk-bg hover:brightness-110 animate-pulse-soft"
              : isLive
                ? "bg-nk-bad text-white hover:bg-nk-bad/90 animate-pulse-soft"
                : role === "qualifier"
                  ? "bg-nk-gold text-nk-bg hover:bg-nk-gold/90"
                  : "bg-nk-gold text-nk-bg hover:bg-nk-gold/90"
          }`}
        >
          {ctaLabel}
          <ArrowRight size={13} weight="bold" />
        </Link>
      </div>
    </div>
  );
}

function formatCountdown(seconds, tick) {
  // tick argument forces re-render every second even if `seconds` prop is stale
  void tick;
  if (seconds <= 0) return "moments";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${String(s).padStart(2, "0")}s`;
  return `${m}m ${String(s).padStart(2, "0")}s`;
}
